import { isEmpty } from "lodash";
import fp from "lodash/fp";

import { CreditReviewInformationFields } from "globalConstants";
import { getFormattedDate, getFullName } from "helpers";
import { float2Normalizer } from "validators";

export const agentStatusTypes = {
	TERMINATED_AGENT: 1,
	PROSPECTIVE_AGENT: 2,
	ACTIVATED_AGENT: 3,
	INACTIVE_AGENT: 4,
};

export const agentsOptionLabel = option => {
	if (option?.active) {
		return getFullName(option);
	} else {
		return `${getFullName(option)} ${"(Inactive)"}`;
	}
};

export const ageInUWLabel = values => {
	const getMinDaysValue = fp.get("min_days", values);
	const getMinHoursValue = fp.get("min_hours", values);
	const getMinMinutesValue = fp.get("min_minutes", values);

	const getMaxDaysValue = fp.get("max_days", values);
	const getMaxHoursValue = fp.get("max_hours", values);
	const getMaxMinutesValue = fp.get("max_minutes", values);

	const isAgeMinimum =
		!isEmpty(getMinDaysValue) ||
		!isEmpty(getMinHoursValue) ||
		!isEmpty(getMinMinutesValue);
	const isAgeMaximin =
		!isEmpty(getMaxDaysValue) ||
		!isEmpty(getMaxHoursValue) ||
		!isEmpty(getMaxMinutesValue);

	const minDaysValue = getMinDaysValue
		? `${getMinDaysValue} ${
				parseInt(getMinDaysValue) === 1 ? "day;" : "days;"
		  }`
		: "";
	const minHoursValue = getMinHoursValue ? `${getMinHoursValue} h;` : "";
	const minMinutesValue = getMinMinutesValue
		? `${getMinMinutesValue} min;`
		: "";
	const maxDaysValue = getMaxDaysValue
		? `${getMaxDaysValue} ${
				parseInt(getMaxDaysValue) === 1 ? "day;" : "days;"
		  }`
		: "";
	const maxHoursValue = getMaxHoursValue ? `${getMaxHoursValue} h;` : "";
	const maxMinutesValue = getMaxMinutesValue
		? `${getMaxMinutesValue} min;`
		: "";

	if (isAgeMaximin && isAgeMinimum) {
		return `${minDaysValue} ${minHoursValue} ${minMinutesValue} - 
		${maxDaysValue} ${maxHoursValue} ${maxMinutesValue}`;
	} else if (isAgeMinimum && !isAgeMaximin) {
		return `Min ${minDaysValue} ${minHoursValue} ${minMinutesValue}`;
	} else if (isAgeMaximin && !isAgeMinimum) {
		return `Max ${maxDaysValue} ${maxHoursValue} ${maxMinutesValue}`;
	} else return;
};

export const getTradesAtmType1Columns = (
	accountNumber,
	open,
	monthPay,
	acctcond,
) => [
	{
		label: "Account",
		value: accountNumber as string,
	},
	{
		label: "OPEN",
		value: getFormattedDate({ date: open, format: "MM/DD/YYYY" }),
	},
	{
		label: "MONTHLY PAY",
		value: float2Normalizer(monthPay, "$") || "N/A",
	},
	{
		label: "ACCTCOND",
		value: acctcond as string,
	},
];

export const getTradesSubscribersColumns = (
	subNumber,
	ecoa,
	balance,
	pastDue,
	pymtHistory,
) => [
	{
		label: "SUB#",
		value: subNumber as string,
	},
	{
		label: "ECOA",
		value: ecoa as string,
	},
	{
		label: "BALANCE",
		value: float2Normalizer(balance, "$") || "N/A",
	},
	{
		label: "PAST DUE",
		value: float2Normalizer(pastDue, "$") || "N/A",
	},
	{
		label: "PYMT HISTORY BY MONTH",
		value: pymtHistory,
	},
];

export const getTradesPaymentColumns = (
	kob,
	balDate,
	amttyP2,
	mosrev,
	amtType2Type,
) => [
	{
		label: "KOB",
		value: kob as string,
	},
	{
		label: "BALDATE",
		value: getFormattedDate({ date: balDate, format: "MM/DD/YYYY" }),
	},
	{
		label: "AMT-TYP2",
		value:
			amttyP2 !== null && amttyP2 !== undefined
				? `${amtType2Type} ${float2Normalizer(amttyP2, "$")}`
				: "N/A",
	},
	{
		label: "MOS REV",
		value: mosrev as string,
	},
];

export const getTradesTypColumns = (trm, lastPD, pymtLevel, maximum) => [
	{
		label: "TERM",
		value: trm as string,
	},
	{
		label: "LAST PD",
		value: getFormattedDate({ date: lastPD, format: "MM/DD/YYYY" }),
	},
	{
		label: "PYMT LEVEL",
		value: getFormattedDate({ date: pymtLevel, format: "MM/DD/YYYY" }),
	},
	{
		label: "MAXIMUM",
		value: getFormattedDate({ date: maximum, format: "MM/DD/YYYY" }),
	},
];

export const setCreditReviewInformation = (data: {
	[key: string]: any;
}): { [key: string]: any } => {
	return Object.values(CreditReviewInformationFields).reduce((acc, cur) => {
		return { ...acc, [cur]: data?.[cur] };
	}, {});
};
